import * as React from 'react';

import { rem } from '../../tokens/spacing';

import {
  ShellRoot,
  ShellBannerSlot,
  ShellBody,
  ShellOverlay,
} from './AppShell.styled';
import { AppShellProvider } from './useAppShell';
import type { AppShellVariant, AppShellContextValue } from './useAppShell';
import { AppShellMain } from './AppShell.Main';
import type { MainPadding } from './AppShell.styled';
import { AppShellSidebar, SIDEBAR_SYMBOL } from './AppShell.Sidebar';
import type { AppShellSidebarProps } from './AppShell.Sidebar';

/* ─────────────────────────── Types ──────────────────────────────────────── */

export interface AppShellProps {
  navbar?: React.ReactNode;
  banner?: React.ReactNode;
  sidebar?: React.ReactNode;
  footer?: React.ReactNode;
  overlay?: React.ReactNode;
  children: React.ReactNode;

  backgroundImage?: string;
  navbarHeight?: string;
  /** Padding for the main content area. Defaults based on variant. */
  padding?: MainPadding;
  variant?: AppShellVariant;
  mainId?: string;
  className?: string;
  'data-testid'?: string;
}

/* ─────────────────────────── Helpers ────────────────────────────────────── */

function isSidebarElement(
  child: React.ReactNode,
): child is React.ReactElement<AppShellSidebarProps> {
  return (
    React.isValidElement(child) &&
    typeof child.type !== 'string' &&
    (child.type as any).__SHELL_SIDEBAR__ === SIDEBAR_SYMBOL
  );
}

function extractChildren(children: React.ReactNode): {
  sidebar: React.ReactElement<AppShellSidebarProps> | null;
  rest: React.ReactNode[];
} {
  let sidebar: React.ReactElement<AppShellSidebarProps> | null = null;
  const rest: React.ReactNode[] = [];

  React.Children.forEach(children, (child) => {
    if (sidebar == null && isSidebarElement(child)) {
      sidebar = child;
    } else {
      rest.push(child);
    }
  });

  return { sidebar, rest };
}

/* ─────────────────────────── Component ──────────────────────────────────── */

function AppShellRoot(props: AppShellProps): JSX.Element {
  const {
    navbar,
    banner,
    sidebar: sidebarProp,
    footer,
    overlay,
    children,
    backgroundImage,
    navbarHeight = rem(67),
    padding,
    variant = 'default',
    mainId,
    className,
    'data-testid': testId,
  } = props;

  const { sidebar: sidebarChild, rest: mainChildren } = extractChildren(children);

  const sidebarElement = sidebarProp ?? sidebarChild;
  const hasSidebar = sidebarElement != null;

  const sidebarCollapsed =
    hasSidebar && React.isValidElement(sidebarElement)
      ? Boolean((sidebarElement.props as AppShellSidebarProps).collapsed)
      : false;

  const sidebarWidth = !hasSidebar
    ? '0px'
    : sidebarCollapsed
      ? (React.isValidElement(sidebarElement)
          ? (sidebarElement.props as AppShellSidebarProps).collapsedWidth
          : undefined) ?? rem(64)
      : (React.isValidElement(sidebarElement)
          ? (sidebarElement.props as AppShellSidebarProps).width
          : undefined) ?? rem(240);

  const contextValue = React.useMemo<AppShellContextValue>(
    () => ({
      navbarHeight,
      sidebarWidth,
      sidebarCollapsed,
      variant,
    }),
    [navbarHeight, sidebarWidth, sidebarCollapsed, variant],
  );

  const cssVars = {
    '--shell-navbar-h': navbarHeight,
    '--shell-sidebar-w': sidebarWidth,
  } as React.CSSProperties;

  return (
    <AppShellProvider value={contextValue}>
      <ShellRoot className={className} data-testid={testId} style={cssVars}>
        {banner != null && <ShellBannerSlot>{banner}</ShellBannerSlot>}

        {navbar}

        <ShellBody>
          {hasSidebar && sidebarElement}

          <AppShellMain
            id={mainId}
            backgroundImage={backgroundImage}
            padding={padding}
            footer={footer}
          >
            {sidebarChild != null ? mainChildren : children}
          </AppShellMain>
        </ShellBody>

        {overlay != null && <ShellOverlay>{overlay}</ShellOverlay>}
      </ShellRoot>
    </AppShellProvider>
  );
}

AppShellRoot.displayName = 'AppShell';

/* ─────────────────────────── Attach sub-components ──────────────────────── */

type AppShellComponent = typeof AppShellRoot & {
  Sidebar: typeof AppShellSidebar;
  Main: typeof AppShellMain;
};

const AppShell = AppShellRoot as AppShellComponent;
AppShell.Sidebar = AppShellSidebar;
AppShell.Main = AppShellMain;

export { AppShell };
